import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  useToast,
  useBreakpointValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import RefreshProgressCard from './table/RefreshProgressCard';
import { useAuth } from '../../contexts/AuthContext';
import { useInsufficientCredits } from '../../contexts/InsufficientCreditsContext';
import chatService from '../../services/chatService';
import { campaignService } from '../../services/campaignService';
import api from '../../services/api';
import VirtualizedPersonaResults from './VirtualizedPersonaResults';
import CollapsibleChatPanel from './chat/CollapsibleChatPanel';
import ThreadView from './chat/ThreadView';
import TemplateGallery from './templates/TemplateGallery';
import TEMPLATES from './templates/templateConfig';
import useChatStream from '../../hooks/useChatStream';
import useChatMessages from '../../hooks/useChatMessages';
import useMessageEditing from '../../hooks/useMessageEditing';
import useCustomFieldOrchestration from '../../hooks/useCustomFieldOrchestration';
import useStreamObserver from '../../hooks/useStreamObserver';
import useCreditWarning from '../../hooks/useCreditWarning';
import CreditWarningModal from '../common/CreditWarningModal';

/** Maps backend operation_id (FanoutRunner process_name) to status bar bucket. */
function statusBarTypeForOperationId(operationId) {
  if (!operationId) return 'campaign';
  if (
    operationId === 'custom_field_generation' ||
    operationId === 'contact_custom_field_generation' ||
    operationId === 'linkedin_profile_fetch'
  ) {
    return 'custom_fields';
  }
  if (
    operationId === 'email_drafting' ||
    operationId === 'email_draft_generation' ||
    operationId.endsWith('_draft')
  ) {
    return 'email_drafts';
  }
  if (operationId === 'crm_import') return 'crm_import';
  return 'campaign';
}

/** Campaign workspace: results table + chat. SSE `credit_usage_warning` is not shown as a banner. */
const CampaignChatInterface = ({ 
  campaignId: initialCampaignId, 
  onCampaignSaved, 
  onNavigate,
  autoSendWelcomeMessage = false,
}) => {
  const navigate = useNavigate();
  const [campaignId, setCampaignId] = useState(initialCampaignId);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isDetectingTemplate, setIsDetectingTemplate] = useState(Boolean(initialCampaignId));
  
  // Track if we should show thinking state after reconnection
  const [isReconnectedThinking, setIsReconnectedThinking] = useState(false);
  
  // Track if textarea is at max height
  const [isTextareaAtMaxHeight, setIsTextareaAtMaxHeight] = useState(false);

  // File upload state
  const [isUploading, setIsUploading] = useState(false);

  // Whether the current campaign has any entities — controls + button visibility
  const [, setCampaignHasEntities] = useState(false);
  
  // Status tracking - per-operation map so concurrent streams don't clobber each other.
  // Keyed by operation_id (= process_name from the backend), e.g. "custom_field_generation",
  // "down_selection", "company_streaming_add". Falls back to operationType / 'campaign' for
  // legacy events that don't include an operation_id field.
  const [statusUpdate, setStatusUpdate] = useState(null);
  const [statusOperationType, setStatusOperationType] = useState('campaign');
  const activeOpsRef = useRef(new Map()); // operation_id → { status, type }
  // Tracks ops that have already fired onComplete so that duplicate 100 % events
  // arriving from a second subscriber (observer EventBus) don't re-trigger the
  // completion lifecycle. Cleared for an op when a sub-100 % update arrives,
  // signalling that a fresh run has started.
  const completedOpsRef = useRef(new Set());

  // Derive + publish the dominant status from the operations map.
  // Prefers a still-running (< 100 %) operation over a completed one so that
  // the status bar stays visible while any work is outstanding.
  const _syncStatusFromMap = useCallback(() => {
    const entries = Array.from(activeOpsRef.current.entries());
    const activeEntry = entries.find(
      ([, v]) => parseInt(v.status?.completion_percentage ?? 0) < 100,
    );
    const dominant = activeEntry ?? entries[entries.length - 1];
    if (dominant) {
      setStatusUpdate(dominant[1].status);
      setStatusOperationType(dominant[1].type);
    } else {
      setStatusUpdate(null);
      setStatusOperationType('campaign');
    }
  }, []);

  // Replace all (setStatusUpdate + setStatusOperationType) call-sites with this helper.
  const applyOperationStatus = useCallback((status, type) => {
    if (!status) {
      activeOpsRef.current.clear();
      completedOpsRef.current.clear();
      setStatusUpdate(null);
      setStatusOperationType('campaign');
      return;
    }
    const opId = status.operation_id || type || 'campaign';
    const pct = parseInt(status.completion_percentage ?? 0) || 0;

    if (pct >= 100 && completedOpsRef.current.has(opId)) {
      // Duplicate completion event from a second EventBus subscriber — ignore.
      return;
    }

    // A sub-100 % update means a new run has started; allow completion again.
    if (pct < 100) {
      completedOpsRef.current.delete(opId);
    }

    activeOpsRef.current.set(opId, { status, type: type || 'campaign' });
    _syncStatusFromMap();
  }, [_syncStatusFromMap]);

  // Clear a single operation slot (after it completes) and re-derive the dominant status.
  // Pass no argument to clear everything (error / manual reset).
  const clearOperationStatus = useCallback((opId) => {
    if (opId) {
      activeOpsRef.current.delete(opId);
      completedOpsRef.current.add(opId); // prevent duplicate completions
    } else {
      activeOpsRef.current.clear();
      completedOpsRef.current.clear();
    }
    _syncStatusFromMap();
  }, [_syncStatusFromMap]);
  
  // Track chat panel expanded state - single source of truth  
  // Always start expanded on page load/refresh for consistent layout
  // User can collapse during their session; state is preserved within session
  const [isChatExpanded, setIsChatExpanded] = useState(true);
  
  // Panel mode: 'chat' for AI chat, 'message' for message drafting
  const [panelMode, setPanelMode] = useState('chat');
  const [scribelessOrientation, setScribelessOrientation] = useState('landscape');
  const [scribelessTemplate, setScribelessTemplate] = useState('signature');
  // Which side of the card the preview is showing ('front' | 'back')
  const [scribelessSide, setScribelessSide] = useState('front');
  // Brand materials to preview on the card back ({ logo, tagline } | null)
  const [scribelessBrand, setScribelessBrand] = useState(null);
  // Selected message body to preview on the card front (string | null)
  const [scribelessNoteBody, setScribelessNoteBody] = useState(null);
  // Auto-shrink: handwriting font size (px) computed by fitting the note to the
  // writing area. Mirrors how the printed card scales the font down to fit.
  const NOTE_IDEAL_PX = 23;   // ideal Caveat size in the preview
  const NOTE_MIN_PX = 15;     // floor — below this it stops being legible/handwritten
  const NOTE_LINE_RATIO = 1.5;
  // Uniform zoom for the card preview. The card's internal layout is authored at
  // the base size; this scales the whole thing up so it reads better in the view.
  const SCRIBELESS_CARD_SCALE = 1.35;
  const [noteFontPx, setNoteFontPx] = useState(NOTE_IDEAL_PX);
  const noteTextRef = useRef(null);

  // Fit the note text to its box by stepping the font size down from the ideal
  // until it no longer overflows (or hits the floor). Measures real rendered
  // height, so newlines, wrapping and long content are all handled.
  const fitNoteToBox = useCallback(() => {
    const el = noteTextRef.current;
    if (!el) return;
    let px = NOTE_IDEAL_PX;
    const apply = (size) => {
      el.style.fontSize = `${size}px`;
      el.style.lineHeight = `${Math.round(size * NOTE_LINE_RATIO)}px`;
    };
    apply(px);
    while (px > NOTE_MIN_PX && el.scrollHeight > el.clientHeight) {
      px -= 1;
      apply(px);
    }
    setNoteFontPx(px);
  }, []);

  // Re-fit on content/layout changes. Note: when the orientation changes the
  // card resizes via a spring animation, so the box isn't at its final size
  // yet here — the card's onAnimationComplete re-runs fitNoteToBox once the
  // box has settled, which is the source of truth for the final fit.
  useLayoutEffect(() => {
    fitNoteToBox();
  }, [fitNoteToBox, scribelessNoteBody, scribelessSide, scribelessTemplate, scribelessOrientation, scribelessBrand]);

  /** Active credit spend gate prompt from chat SSE (agent tools). */
  const [chatCreditGate, setChatCreditGate] = useState(null);
  const chatCreditGateRef = useRef(null);
  useEffect(() => {
    chatCreditGateRef.current = chatCreditGate;
  }, [chatCreditGate]);

  // Thread selected from InboxPanel — drives the left-side ThreadView
  const [selectedInboxThread, setSelectedInboxThread] = useState(null);
  
  // Message drafts state - stores saved message templates for immediate column rendering
  const [messageDrafts, setMessageDrafts] = useState([]);
  const messageDraftsLoadedRef = useRef(null); // track which campaignId we've loaded for
  
  // Export tracking state - per-row export badges next to message group columns
  const [exportTracking, setExportTracking] = useState([]);
  
  // Enable transitions only after layout has stabilized
  const [enableTransitions, setEnableTransitions] = useState(false);
  
  // Chat panel dimensions - static values for consistent layout
  const chatPanelWidth = 480;
  const chatNotchWidth = 46;

  // Check if mobile - default to false (desktop) for initial render stability
  const isMobile = useBreakpointValue({ base: true, lg: false }) ?? false;
  
  // Enable transitions after initial render to prevent layout shift on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setEnableTransitions(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Load message drafts + export tracking from backend when campaign opens
  useEffect(() => {
    if (!campaignId || messageDraftsLoadedRef.current === campaignId) return;
    messageDraftsLoadedRef.current = campaignId;

    const loadDrafts = async () => {
      try {
        const response = await campaignService.getMessageDrafts(campaignId);
        setMessageDrafts(response?.drafts ?? []);
      } catch (error) {
        console.error('[CampaignChatInterface] Failed to load message drafts:', error);
      }
    };

    const loadExportTracking = async () => {
      try {
        const response = await campaignService.getExportTracking(campaignId);
        setExportTracking(response?.exports || []);
      } catch (error) {
        // Non-critical — table renders without badges
      }
    };

    // Fire both in parallel — independent data
    loadDrafts();
    loadExportTracking();
  }, [campaignId]);

  // Refresh export tracking (called after a successful export)
  const refreshExportTracking = useCallback(async () => {
    if (!campaignId) return;
    try {
      const response = await campaignService.getExportTracking(campaignId);
      setExportTracking(response?.exports || []);
    } catch (_) { /* non-critical */ }
  }, [campaignId]);

  // Refresh message drafts (called after email draft approval completes)
  const refreshMessageDrafts = useCallback(async () => {
    if (!campaignId) return;
    try {
      const response = await campaignService.getMessageDrafts(campaignId);
      setMessageDrafts(response?.drafts ?? []);
    } catch (error) {
      console.error('[CampaignChatInterface] Failed to refresh message drafts:', error);
    }
  }, [campaignId]);
  
  const handleTemplateSelect = useCallback((template) => {
    setSelectedTemplate(template);
  }, []);

  // Toggle chat panel
  const handleToggleChatPanel = useCallback(() => {
    setIsChatExpanded(prev => !prev);
  }, []);

  // Handle panel mode change (chat <-> message drafting)
  const handlePanelModeChange = useCallback((newMode) => {
    setPanelMode(newMode);
    // Clear edit context when leaving the message tab (opening message must not drop onMessageDraftEdit state)
    if (newMode !== 'message') {
      setEditingDraft(null);
    }
    // Leaving inbox — clear any open thread so the left panel resets cleanly
    if (newMode !== 'inbox') {
      setSelectedInboxThread(null);
    }
    // Auto-expand panel when switching modes
    if (!isChatExpanded) {
      setIsChatExpanded(true);
    }
  }, [isChatExpanded]);
  
  // Track if welcome message has been sent (to prevent double-sending)
  const welcomeMessageSentRef = useRef(false);
  
  // Track if initial data fetch has been triggered for this campaign
  const initialFetchTriggeredRef = useRef(null);
  
  // Ref to store streaming data handler from VirtualizedPersonaResults
  const streamingDataHandlerRef = useRef(null);

  // Ref to store campaign completion handler from VirtualizedPersonaResults
  const campaignCompletionHandlerRef = useRef(null);
  
  // Ref to store refresh handler from VirtualizedPersonaResults
  const refreshHandlerRef = useRef(null);

  /** Imperative refresh for GillyReach column (same payload as the 15s poller). */
  const refreshGillyreachSequencerContactStatus = useCallback(async () => {
    const fn = refreshHandlerRef.current?.refreshGillyreachSequencerStatus;
    if (typeof fn === 'function') {
      await fn();
    }
  }, []);
  
  // Ref to track status bar hide timeout
  const statusBarHideTimeoutRef = useRef(null);
  
  // Ref to track if we're handling completion (prevent duplicates)
  const isHandlingCompletionRef = useRef(null);

  // Ref populated by VirtualizedPersonaResults — lets handleStop abort the
  // "Run for All" research SSE stream the same way it aborts the chat stream.
  const researchStopRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { fetchUserProfile } = useAuth();
  const { showInsufficientCreditsModal } = useInsufficientCredits();
  const toast = useToast();
  const queryClient = useQueryClient();
  const handleCreditSpendGateFromStream = useCallback((payload) => {
    if (!payload || typeof payload !== 'object') return;
    if (payload.kind === 'prompt') {
      setChatCreditGate(payload);
      setIsChatExpanded(true);
    } else if (payload.kind === 'resolved') {
      setChatCreditGate((g) =>
        g && payload.gate_id && g.gate_id === payload.gate_id ? null : g
      );
    } else if (payload.kind === 'blocked') {
      setChatCreditGate(null);
      showInsufficientCreditsModal(payload.detail);
    }
  }, [showInsufficientCreditsModal]);

  const submitChatCreditGate = useCallback(async (decision) => {
    const gate = chatCreditGateRef.current;
    if (!campaignId || !gate?.gate_id) return;
    try {
      await api.post(`/api/campaigns/${campaignId}/credit-gate/respond`, {
        gate_id: gate.gate_id,
        decision,
      });
    } catch (err) {
      console.error('[credit gate respond]', err);
      toast({
        title: 'Could not send response',
        status: 'error',
        duration: 2500,
      });
    }
    if (decision === 'reject') {
      setChatCreditGate(null);
      window.dispatchEvent(new CustomEvent('creditWarningCancelled'));
    }
  }, [campaignId, toast]);
  const {
    withCreditWarning,
    isWarningOpen,
    warningData,
    handleConfirm,
    handleOptOut,
    closeWarning,
    userSubscriptionStatus,
  } = useCreditWarning();

  // Handle message drafting preview (placeholder - will be implemented in Phase 2)
  const handleMessagePreview = useCallback(async (messageData) => {
    console.log('[CampaignChatInterface] Message preview:', messageData);
    // TODO: Implement preview on 5 sample rows
    toast({
      title: 'Preview',
      description: 'Preview functionality coming in Phase 2',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  }, [toast]);

  // State for editing an existing message draft
  const [editingDraft, setEditingDraft] = useState(null);

  // Handle message draft edit - opens panel with pre-filled data
  const handleMessageDraftEdit = useCallback((draft) => {
    console.log('[CampaignChatInterface] Edit draft:', draft);
    setEditingDraft(draft);
    setPanelMode('message');
    if (!isChatExpanded) {
      setIsChatExpanded(true);
    }
  }, [isChatExpanded]);

  // Handle message draft rename — optimistic UI + backend call
  const handleMessageDraftRename = useCallback(async (draftId, newName) => {
    // Optimistic: update UI immediately
    setMessageDrafts(prev => prev.map(draft => 
      draft.id === draftId ? { ...draft, emailName: newName } : draft
    ));
    toast({ title: 'Column renamed', description: `Renamed to "${newName}"`, status: 'success', duration: 2000, isClosable: true });

    // Persist to backend (fire-and-forget with error handling)
    try {
      if (campaignId) {
        await campaignService.renameMessageDraft(campaignId, draftId, newName);
      }
    } catch (error) {
      console.error('[CampaignChatInterface] Rename API failed:', error);
      // Don't rollback — rename is cosmetic, will sync on next load
    }
  }, [campaignId, toast]);

  // Handle message draft delete — optimistic UI + backend call
  const handleMessageDraftDelete = useCallback(async (draftId) => {
    // Snapshot for rollback
    const prevDrafts = messageDrafts;

    // Prefer bodyFieldId (always a real MongoDB ObjectId) over the draft's own id,
    // which may still be a temporary "pending_..." value when the user deletes quickly.
    const draft = messageDrafts.find(d => d.id === draftId);
    const apiId = draft?.bodyFieldId || draft?.subjectFieldId || draftId;

    // Optimistic: remove from UI immediately
    setMessageDrafts(prev => prev.filter(draft => draft.id !== draftId));
    toast({ title: 'Message deleted', description: 'Subject and Body columns have been removed.', status: 'info', duration: 2000, isClosable: true });

    // If the draft was never persisted (still pending), there is nothing to delete on the backend.
    if (!apiId || apiId.startsWith('pending_')) return;

    try {
      if (campaignId) {
        await campaignService.deleteMessageDraft(campaignId, apiId);
      }
    } catch (error) {
      console.error('[CampaignChatInterface] Delete API failed:', error);
      // Rollback on failure
      setMessageDrafts(prevDrafts);
      toast({ title: 'Delete failed', description: 'Could not delete. Please try again.', status: 'error', duration: 3000, isClosable: true });
    }
  }, [campaignId, messageDrafts, toast]);

  // Handle message draft description update — optimistic UI + backend call
  const handleMessageDraftDescriptionUpdate = useCallback(async (draftId, description) => {
    setMessageDrafts(prev => prev.map(draft =>
      draft.id === draftId ? { ...draft, description } : draft
    ));
    toast({ title: 'Description saved', status: 'success', duration: 2000, isClosable: true });

    try {
      if (campaignId) {
        await campaignService.updateMessageDraftDescription(campaignId, draftId, description);
      }
    } catch (error) {
      console.error('[CampaignChatInterface] Description API failed:', error);
    }
  }, [campaignId, toast]);

  // Save or update message draft — backend call + UI update
  const handleMessageSaveOrUpdate = useCallback(async (messageData) => {
    const apiPayload = {
      email_name: messageData.emailName,
      type_of_message: messageData.typeOfMessage,
      message_type: messageData.messageType,
      enable_html: messageData.enableHtml,
      subject_template: messageData.subject,
      body_template: messageData.body,
    };
    if (messageData.aiPrompt !== undefined && messageData.aiPrompt !== null) {
      apiPayload.ai_prompt = messageData.aiPrompt;
    }

    if (editingDraft) {
      // ── UPDATE existing draft ──
      // Optimistic UI update
      setMessageDrafts(prev => prev.map(draft =>
        draft.id === editingDraft.id
          ? {
              ...draft,
              typeOfMessage: messageData.typeOfMessage,
              emailName: messageData.emailName,
              subjectTemplate: messageData.subject,
              bodyTemplate: messageData.body,
              messageType: messageData.messageType,
              enableHtml: messageData.enableHtml,
              ...(messageData.aiPrompt !== undefined && messageData.aiPrompt !== null
                ? { aiPrompt: messageData.aiPrompt }
                : {}),
            }
          : draft
      ));
      setEditingDraft(null);
      setPanelMode('chat');
      toast({ title: 'Message updated', description: `"${messageData.emailName}" columns updated.`, status: 'success', duration: 3000, isClosable: true });

      // Backend call — table cells read resolved values from contacts fetch; refetch like create path
      try {
        if (campaignId) {
          await campaignService.updateMessageDraft(campaignId, editingDraft.id, apiPayload);
          if (refreshHandlerRef.current?.refresh) {
            await refreshHandlerRef.current.refresh();
          }
          // Plan A — template-level edit. The backend's
          // `_resolve_and_store_values` rewrites every contact's
          // (subject, body) for this draft, so every previously
          // reviewed cell on this column is now stale. The contacts
          // list refetch above already updates the table; this
          // invalidation refreshes the chip strip so its counts
          // reflect the bulk transition (Compliant -= N, Stale += N).
          queryClient.invalidateQueries({ queryKey: ['complianceSummary', campaignId] });
        }
      } catch (error) {
        console.error('[CampaignChatInterface] Update API failed:', error);
        toast({ title: 'Sync failed', description: 'Changes saved locally but backend sync failed.', status: 'warning', duration: 3000, isClosable: true });
      }
    } else {
      // ── CREATE new draft ──
      // Optimistic: add placeholder to UI immediately
      const tempId = `pending_${Date.now()}`;
      const newDraft = {
        id: tempId,
        typeOfMessage: messageData.typeOfMessage,
        emailName: messageData.emailName,
        subjectTemplate: messageData.subject,
        bodyTemplate: messageData.body,
        messageType: messageData.messageType,
        enableHtml: messageData.enableHtml,
        subjectFieldId: null,
        bodyFieldId: null,
        status: 'draft',
      };
      setMessageDrafts(prev => [...prev, newDraft]);
      setPanelMode('chat');
      toast({ title: 'Message template saved', description: `Columns created for "${messageData.emailName}".`, status: 'success', duration: 3000, isClosable: true });

      // Backend call — replace temp ID with real message_group_id
      try {
        if (campaignId) {
          const result = await campaignService.createMessageDraft(campaignId, apiPayload);
          // Swap temp ID → real ID from backend, and pick up de-duplicated emailName
          setMessageDrafts(prev => prev.map(draft =>
            draft.id === tempId
              ? {
                  ...draft,
                  id: result.message_group_id,
                  emailName: result.email_name || draft.emailName,
                  subjectFieldId: result.subject_field_id,
                  bodyFieldId: result.body_field_id,
                  status: 'completed',
                }
              : draft
          ));
          if (refreshHandlerRef.current?.refresh) {
            await refreshHandlerRef.current.refresh();
          }
          // Plan A — adding a draft increases `num_drafts`, which
          // changes `total = total_contacts × num_drafts` and adds
          // `total_contacts` worth of new (contact, draft) cells —
          // all initially in the `not_reviewed` bucket. Refresh the
          // chip strip so the new totals/buckets show.
          queryClient.invalidateQueries({ queryKey: ['complianceSummary', campaignId] });
        }
      } catch (error) {
        console.error('[CampaignChatInterface] Create API failed:', error);
        toast({ title: 'Sync failed', description: 'Columns visible locally but backend save failed.', status: 'warning', duration: 3000, isClosable: true });
      }
    }
  }, [editingDraft, campaignId, toast, queryClient]);

  // Use chat messages hook
  const {
    messages,
    setMessages,
    loadChatHistory,
    addMessage,
  } = useChatMessages(campaignId);

  // Use message editing hook
  const {
    editingMessageId,
    editingContent,
    editTextareaRef,
    editBubbleRef,
    startEdit,
    cancelEdit,
    updateContent,
  } = useMessageEditing();

  // Handle unauthorized logout
  const handleUnauthorizedLogout = useCallback(() => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  }, []);

  // Track if we're creating a new campaign (to prevent race condition with loadChatHistory)
  const isCreatingCampaignRef = useRef(false);

  // Use chat stream hook
  const {
    sendMessage: sendChatMessage,
    stopStream,
    isStreaming,
    isStreamOpen,
  } = useChatStream({
    onTokenReceived: (content) => {
      // Update the streaming message with new content
      setMessages(prev => {
        const lastMessage = prev[prev.length - 1];
        
        if (!lastMessage || lastMessage.role !== 'assistant' || lastMessage.functionCall) {
          // Create new assistant message
          return [...prev, {
            id: prev.length,
            role: 'assistant',
            content: content,
            timestamp: new Date(),
            canRevert: false,
            isStreaming: true
          }];
        }
        
        // Update existing streaming message
        return prev.map((msg, idx) => 
          idx === prev.length - 1 && msg.isStreaming
            ? { ...msg, content }
            : msg
        );
      });
    },
    onDataEvent: (data) => {
      // Handle data events from chat stream
      if (data.event === 'companies' || data.event === 'contacts') {
      if (streamingDataHandlerRef.current) {
          const paginationInfo = data.data?.pagination_info;
          const eventType = data.event === 'companies' ? 'companies_data_ready' : 'contacts_data_ready';
          
          streamingDataHandlerRef.current({
            type: eventType,
            data: {
              count: paginationInfo?.[data.event === 'companies' ? 'total_companies' : 'total_contacts'] ?? 0,
              [data.event]: data.data?.[data.event] || [],
              version_id: paginationInfo?.version_id || null,
              pagination_info: paginationInfo
            }
          });
          
          // Also update the other count
          const otherType = data.event === 'companies' ? 'contacts' : 'companies';
          const otherCount = paginationInfo?.[`total_${otherType}`];
          if (otherCount !== undefined) {
          streamingDataHandlerRef.current({
              type: `${otherType}_data_ready`,
              data: {
                count: otherCount,
                [otherType]: [],
                version_id: paginationInfo?.version_id || null,
                pagination_info: paginationInfo
              }
            });
          }
        }
      } else if (data.event === 'status') {
        const opType = statusBarTypeForOperationId(data.data?.operation_id);
        applyOperationStatus(data.data, opType);
        
        // Also forward status events to VirtualizedPersonaResults for custom field generation tracking
        if (streamingDataHandlerRef.current) {
          streamingDataHandlerRef.current({
            type: 'status',
            data: data.data
          });
        }
      } else if (data.event === 'custom_field_definition' ||
                 data.event === 'contacts_custom_field_update' ||
                 data.event === 'companies_custom_field_update') {
        if (streamingDataHandlerRef.current) {
          const eventType = data.event === 'custom_field_definition' ? 'custom_field_definition' : 'custom_field_update';
          streamingDataHandlerRef.current({
            type: eventType,
            data: data.data
          });
        }
      }
    },
    onStreamStart: (newCampaignId) => {
      if (newCampaignId && !campaignId) {
        isCreatingCampaignRef.current = true;
        setCampaignId(newCampaignId);
        navigate(`/campaign/${newCampaignId}`, { replace: true });
      }
    },
    onStreamEnd: (finalContent) => {
      setIsProcessing(false);
      setMessages(prev => {
        return prev.map(msg => 
          msg.isStreaming ? { 
            ...msg, 
            content: finalContent, 
            isStreaming: false 
          } : msg
        );
      });
      
      // Silent background re-fetch on stream end (no spinner)
      // For new campaigns, the useEffect will handle loading history after campaignId is set
      if (campaignId) {
        // Reload chat history after a delay to get version IDs for edit functionality
        setTimeout(() => {
          loadChatHistory();
        }, 500);
      } else {
        // Clear the creating flag so the next useEffect can handle loading
        setTimeout(() => {
          isCreatingCampaignRef.current = false;
        }, 500);
      }
    },
    onError: (errorMessage) => {
      setIsProcessing(false);
      toast({
        title: 'Chat Error',
        description: errorMessage || 'There was an error sending your message.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
    onUnauthorized: handleUnauthorizedLogout,
    fetchUserProfile,
    onCreditSpendGate: handleCreditSpendGateFromStream,
  });

  // Use custom field orchestration hook
  const customFieldOrchestration = useCustomFieldOrchestration({
    campaignId,
    onStatusUpdate: (status, operationType) => {
      applyOperationStatus(status, operationType);
    },
    onDataUpdate: (data) => {
      if (streamingDataHandlerRef.current) {
        streamingDataHandlerRef.current(data);
      }
    },
    onComplete: async (operationType) => {
      clearOperationStatus();

      if (
        (operationType === 'approval' || operationType === 'preview') &&
        refreshHandlerRef.current?.initialFetch
      ) {
        try {
          await refreshHandlerRef.current.initialFetch();
        } catch (error) {
          console.error('Error refreshing data after custom fields:', error);
        }
      }
    },
    onError: (error) => {
      clearOperationStatus();
      
      // Handle insufficient credits error (402) - show modal instead of toast
      if (error.status === 402) {
        showInsufficientCreditsModal(error.detail ?? error.message ?? 'You do not have enough credits for custom field generation.');
      }
    },
  });

  // Memoize observer callbacks to prevent reconnection loops
  const handleObserverHandshake = useCallback((data) => {
    // If chat was streaming when we reconnected, show thinking state
    if (data.is_chat_streaming) {
      setIsProcessing(true);
      setIsReconnectedThinking(true);
    }
    
    // If there's a cached status, show it and forward to VirtualizedPersonaResults
    if (data.status) {
      const restoredType = statusBarTypeForOperationId(data.status.operation_id);
      applyOperationStatus(data.status, restoredType);
      console.log('Restored status from cache:', data.status);

      // If a company-streaming operation is still in progress, mark as processing
      // so that scroll-guards and the loading UI activate immediately on reconnect.
      const pct = parseInt(data.status.completion_percentage) || 0;
      if (pct < 100) {
        setIsProcessing(true);
      }
      
      // Forward status to VirtualizedPersonaResults to set isGeneratingCustomFields
      if (streamingDataHandlerRef.current) {
        streamingDataHandlerRef.current({
          type: 'status',
          data: data.status
        });
      }
    }
  }, [applyOperationStatus]);
  
  const handleObserverChatEnd = useCallback(async () => {
    console.log('Chat streaming completed, loading chat history');
    setIsProcessing(false);
    setIsReconnectedThinking(false);
    
    // Load the complete message from MongoDB
    if (campaignId) {
      setTimeout(() => {
        loadChatHistory();
      }, 500);
    }
  }, [campaignId, loadChatHistory]);
  
  const handleObserverStatus = useCallback((status) => {
    const statusType = statusBarTypeForOperationId(status?.operation_id);
    applyOperationStatus(status, statusType);

    // When streaming finishes, clear the processing gate so the UI unlocks.
    const pct = parseInt(status.completion_percentage) || 0;
    if (pct >= 100) {
      setIsProcessing(false);
    }
    
    // Also forward status events to VirtualizedPersonaResults
    if (streamingDataHandlerRef.current) {
      streamingDataHandlerRef.current({
        type: 'status',
        data: status
      });
    }
  }, [applyOperationStatus]);
  
  const handleObserverData = useCallback((data) => {
    // Handle all data events from EventBus (companies, contacts, custom fields, etc.)
    if (data.event === 'companies' || data.event === 'contacts') {
      if (streamingDataHandlerRef.current) {
        const paginationInfo = data.data?.pagination_info;
        const eventType = data.event === 'companies' ? 'companies_data_ready' : 'contacts_data_ready';
        
        streamingDataHandlerRef.current({
          type: eventType,
          data: {
            count: paginationInfo?.[data.event === 'companies' ? 'total_companies' : 'total_contacts'] ?? 0,
            [data.event]: data.data?.[data.event] || [],
            version_id: paginationInfo?.version_id || null,
            pagination_info: paginationInfo
          }
        });
        
        // Also update the other count
        const otherType = data.event === 'companies' ? 'contacts' : 'companies';
        const otherCount = paginationInfo?.[`total_${otherType}`];
        if (otherCount !== undefined) {
          streamingDataHandlerRef.current({
            type: `${otherType}_data_ready`,
            data: {
              count: otherCount,
              [otherType]: [],
              version_id: paginationInfo?.version_id || null,
              pagination_info: paginationInfo
            }
          });
        }
      }
    } else if (data.event === 'custom_field_definition' ||
               data.event === 'contacts_custom_field_update' ||
               data.event === 'companies_custom_field_update') {
      if (streamingDataHandlerRef.current) {
        const eventType = data.event === 'custom_field_definition' ? 'custom_field_definition' : 'custom_field_update';
        streamingDataHandlerRef.current({
          type: eventType,
          data: data.data
        });
      }
    } 
  }, []);
  
  const handleObserverError = useCallback((error) => {
    console.error('Stream observer error:', error);
    
    // Clear reconnected thinking state on error
    setIsReconnectedThinking(false);
    
    // Extract error message
    const message = error?.message || error?.toString() || 'Lost connection to campaign updates';
    
    // Only show toast for real errors (not abort errors from navigation)
    if (error && error.name !== 'AbortError') {
      toast({
        title: 'Connection Error',
        description: message,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast]);

  // Use stream observer for reconnection support and ongoing operation monitoring
  useStreamObserver(campaignId, {
    onHandshake: handleObserverHandshake,
    onChatEnd: handleObserverChatEnd,
    onStatus: handleObserverStatus,
    onData: handleObserverData,
    onError: handleObserverError
  });

  useEffect(() => {
    const handler = (e) => handleObserverData(e.detail);
    window.addEventListener('mdpStreamEvent', handler);
    return () => window.removeEventListener('mdpStreamEvent', handler);
  }, [handleObserverData]);

  // Refresh messageDrafts when any email draft approval completes
  // (covers both the table-banner approval and the MessageDraftingPanel approve-stream path)
  useEffect(() => {
    window.addEventListener('emailDraftApproved', refreshMessageDrafts);
    window.addEventListener('messageDraftPreviewRegenerated', refreshMessageDrafts);
    window.addEventListener('messageDraftPreviewRestored', refreshMessageDrafts);
    return () => {
      window.removeEventListener('emailDraftApproved', refreshMessageDrafts);
      window.removeEventListener('messageDraftPreviewRegenerated', refreshMessageDrafts);
      window.removeEventListener('messageDraftPreviewRestored', refreshMessageDrafts);
    };
  }, [refreshMessageDrafts]);
  
  // Initialize component
  useEffect(() => {
    setCampaignId(initialCampaignId);
    setCampaignHasEntities(false); // reset until data loads
  }, [initialCampaignId]);

  // Auto-detect template for existing campaigns (reopened from campaigns list / page refresh).
  // Gates the render via isDetectingTemplate to prevent VirtualizedPersonaResults
  // from mounting before we know the campaign type.
  useEffect(() => {
    if (!campaignId || selectedTemplate) {
      setIsDetectingTemplate(false);
      return;
    }
    setIsDetectingTemplate(true);
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/api/campaigns/${campaignId}/info`);
        if (cancelled) return;
        const tid = res.data?.template_id;
        if (tid) {
          const tmpl = TEMPLATES.find(t => t.id === tid);
          if (tmpl) setSelectedTemplate(tmpl);
        }
      } catch (err) {
        if (!cancelled) console.warn('[CampaignChat] template auto-detect failed:', err?.message);
      } finally {
        if (!cancelled) setIsDetectingTemplate(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId, selectedTemplate]);

  // Load chat history and table data when campaign ID changes
  useEffect(() => {
    if (campaignId) {
      // Only trigger initial fetch once per campaign
      if (initialFetchTriggeredRef.current !== campaignId) {
        // Mark that we've seen this campaign
        initialFetchTriggeredRef.current = campaignId;
        
        // Skip immediate loading if we're in the middle of creating this campaign
        // We'll load after the stream completes
        if (isCreatingCampaignRef.current) {
          // Set up a polling check to load history once creation is complete
          const checkAndLoad = () => {
            if (!isCreatingCampaignRef.current) {
              loadChatHistory();
              
              // Trigger initial data fetch
              const attemptFetch = (attempts = 0) => {
                if (refreshHandlerRef.current?.initialFetch) {
                  refreshHandlerRef.current.initialFetch();
                } else if (attempts < 10) {
                  setTimeout(() => attemptFetch(attempts + 1), 50);
                } else {
                  console.warn('Failed to trigger initial data fetch - handler not ready');
                }
              };
              setTimeout(() => attemptFetch(), 100);
            } else {
              // Check again after a short delay
              setTimeout(checkAndLoad, 100);
            }
          };
          
          // Start checking after a delay to allow stream to complete
          setTimeout(checkAndLoad, 600);
          return;
        }
        
        const loadCampaignData = async () => {
          // Load chat history first
          await loadChatHistory();
          
          // Trigger initial data fetch for existing campaigns
          // Give VirtualizedPersonaResults time to mount and register handlers
          const attemptFetch = (attempts = 0) => {
            if (refreshHandlerRef.current?.initialFetch) {
              refreshHandlerRef.current.initialFetch();
            } else if (attempts < 10) {
              // Retry up to 10 times with 50ms intervals (500ms total)
              setTimeout(() => attemptFetch(attempts + 1), 50);
            } else {
              console.warn('Failed to trigger initial data fetch - handler not ready');
            }
          };
          
          setTimeout(() => attemptFetch(), 100);
        };
        
        loadCampaignData();
      }
    } else {
      setMessages([]);
      initialFetchTriggeredRef.current = null;
    }
  }, [campaignId, loadChatHistory, setMessages]);

  // Reset streaming state when input message changes
  useEffect(() => {
    if (!inputMessage && inputRef.current) {
      inputRef.current.style.height = '40px';
      setIsTextareaAtMaxHeight(false);
    }
  }, [inputMessage]);

  // Clear hide timeout when a NEW (non-complete) status update arrives.
  // Duplicate 100%-completion events (from the stream observer echoing the direct SSE stream)
  // must NOT cancel the hide timer that handleStatusBarComplete already set.
  useEffect(() => {
    if (statusUpdate && statusBarHideTimeoutRef.current) {
      const progress = parseInt(statusUpdate.completion_percentage) || 0;
      if (progress < 100) {
        clearTimeout(statusBarHideTimeoutRef.current);
        statusBarHideTimeoutRef.current = null;
      }
    }
  }, [statusUpdate]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  const handleTextareaChange = useCallback((e) => {
    const textarea = e.target;
    const value = textarea.value;
    
    setInputMessage(value);
    
    textarea.style.height = 'auto';
    const newHeight = Math.min(Math.max(textarea.scrollHeight, 40), 200);
    textarea.style.height = `${newHeight}px`;
    setIsTextareaAtMaxHeight(newHeight === 200);
  }, []);

  // Handle send message
  const handleSendMessage = useCallback(async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Reset textarea height explicitly
    if (inputRef.current) {
      inputRef.current.style.height = '40px';
      setIsTextareaAtMaxHeight(false);
    }
    
    // If this is a new campaign (no campaignId), set the creating flag
    if (!campaignId) {
      isCreatingCampaignRef.current = true;
    }
    
    // Add user message
    addMessage('user', userMessage);

    // Set loading state (isProcessing stays true until stream ends so left panel shows loader)
    setIsLoading(true);
    setIsProcessing(true);
    
    try {
      await sendChatMessage({
        message: userMessage,
        campaignId,
        templateId: selectedTemplate?.id || null
      });
    } catch (error) {
      console.error('Chat error:', error);
      addMessage('system', '❌ Sorry, there was an error processing your message. Please try again.');
      isCreatingCampaignRef.current = false;
      setIsProcessing(false);
    } finally {
      setIsLoading(false);
    }
  }, [inputMessage, isLoading, campaignId, selectedTemplate, addMessage, sendChatMessage]);

  // Stream bulk file upload (single request) and relay SSE events into the UI
  const _streamBulkUpload = useCallback(async (files, activeCampaignId, contextPrompt) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('context_prompt', contextPrompt);

    const token = localStorage.getItem('token');
    const apiBase = process.env.REACT_APP_API_URL || '';

    const response = await fetch(
      `${apiBase}/api/campaigns/${activeCampaignId}/upload-file/stream`,
      {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      }
    );

    if (!response.ok) {
      let detail = `Upload failed (${response.status})`;
      let detailPayload = detail;
      try {
        const errJson = await response.json();
        detail = errJson?.detail || detail;
        detailPayload = errJson?.detail ?? detail;
      } catch (_) {}
      if (response.status === 402) {
        showInsufficientCreditsModal(detailPayload);
        return;
      }
      throw new Error(typeof detail === 'string' ? detail : (detail?.message || 'Upload failed'));
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const raw = line.slice(6).trim();
        if (!raw) continue;

        let event;
        try { event = JSON.parse(raw); } catch (_) { continue; }

        const { event: eventName, data } = event;

        if (eventName === 'status') {
          const opType = statusBarTypeForOperationId(data?.operation_id);
          applyOperationStatus(data, opType);
          if (streamingDataHandlerRef.current) {
            streamingDataHandlerRef.current({ type: 'status', data });
          }
        } else if (eventName === 'companies' || eventName === 'contacts') {
          if (streamingDataHandlerRef.current) {
            const paginationInfo = data?.pagination_info;
            const type = eventName === 'companies' ? 'companies_data_ready' : 'contacts_data_ready';
            streamingDataHandlerRef.current({
              type,
              data: {
                count: paginationInfo?.[eventName === 'companies' ? 'total_companies' : 'total_contacts'] ?? 0,
                [eventName]: data?.[eventName] || [],
                version_id: paginationInfo?.version_id || null,
                pagination_info: paginationInfo,
              },
            });
          }
        } else if (
          eventName === 'custom_field_definition' ||
          eventName === 'contacts_custom_field_update' ||
          eventName === 'companies_custom_field_update'
        ) {
          if (streamingDataHandlerRef.current) {
            const type = eventName === 'custom_field_definition' ? 'custom_field_definition' : 'custom_field_update';
            streamingDataHandlerRef.current({ type, data });
          }
        } else if (eventName === 'end') {
          setTimeout(() => loadChatHistory(), 500);
          break;
        } else if (eventName === 'error') {
          throw new Error(data?.message || 'Upload processing error');
        }
      }
    }
  }, [loadChatHistory, showInsufficientCreditsModal, applyOperationStatus]);

  // Handle file upload(s) with mandatory context prompt
  const handleFileUpload = useCallback(async (filesInput, contextPrompt) => {
    const files = Array.isArray(filesInput) ? filesInput : [filesInput];
    const trimmedContext = (contextPrompt || '').trim();

    if (!trimmedContext) {
      toast({
        title: 'Upload context required',
        description: 'Please provide context to help entity resolution before uploading.',
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    try {
      await withCreditWarning(
        'file_upload',
        { qty: files.length },
        async () => {
          setIsUploading(true);

          let activeCampaignId = campaignId;

          // Auto-create a blank campaign when the user uploads without an active campaign
          if (!activeCampaignId) {
            try {
              const res = await api.post('/api/campaigns');
              activeCampaignId = res.data?.campaign_id;
              if (!activeCampaignId) throw new Error('No campaign_id returned');
              isCreatingCampaignRef.current = true;
              setCampaignId(activeCampaignId);
              navigate(`/campaign/${activeCampaignId}`, { replace: true });
            } catch (err) {
              console.error('[handleFileUpload] campaign creation failed:', err);
              toast({
                title: 'Could not create campaign',
                description: err.message || 'Please try again.',
                status: 'error',
                duration: 4000,
                isClosable: true,
              });
              setIsUploading(false);
              return;
            }
          }

          const errors = [];
          try {
            await _streamBulkUpload(files, activeCampaignId, trimmedContext);
          } catch (err) {
            console.error('[handleFileUpload] bulk upload failed:', err);
            errors.push({ name: files.length === 1 ? files[0].name : `${files.length} files`, message: err.message });
          }

          isCreatingCampaignRef.current = false;

          if (errors.length === 0) {
            toast({
              title: files.length === 1 ? 'Upload complete' : `${files.length} files uploaded`,
              description: 'Entities have been added to your campaign.',
              status: 'success',
              duration: 4000,
              isClosable: true,
            });
          } else if (errors.length < files.length) {
            toast({
              title: 'Some uploads failed',
              description: errors.map(e => `${e.name}: ${e.message}`).join('\n'),
              status: 'warning',
              duration: 6000,
              isClosable: true,
            });
          } else {
            toast({
              title: 'Upload failed',
              description: errors[0]?.message || 'There was an error processing your file.',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          }

          setIsUploading(false);
        }
      );
    } catch (err) {
      if (!err?.isCancellation) {
        console.error('[handleFileUpload] withCreditWarning error:', err);
      }
      setIsUploading(false);
    }
  }, [campaignId, _streamBulkUpload, toast, withCreditWarning, navigate]);

  // Handle edit message submit
  const handleSubmitEdit = useCallback(async (message) => {
    if (!editingContent.trim() || isLoading) return;

    const newContent = editingContent.trim();
    const messageIndex = messages.findIndex(msg => msg.id === message.id);
    
    if (messageIndex === -1) {
      toast({
        title: 'Error',
        description: 'Message not found in conversation history',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Optimistically update the message and remove all messages after it
    setMessages(prev => {
      const updatedMessages = [];
      for (let i = 0; i <= messageIndex; i++) {
        if (i === messageIndex) {
          updatedMessages.push({ ...prev[i], content: newContent });
        } else {
          updatedMessages.push(prev[i]);
        }
      }
      return updatedMessages;
    });

    // Clear editing state and set loading
    cancelEdit();
    setIsLoading(true);
    setIsProcessing(true);

    try {
      // Call the edit-and-continue endpoint
      await chatService.editAndContinue(campaignId, messageIndex, newContent, message.versionId);

      // Refresh campaign data
      if (refreshHandlerRef.current?.refresh) {
        await refreshHandlerRef.current.refresh();
      }
      // Rollback/version bump can replace message-draft field defs; keep subject/body
      // field IDs in sync with attach_custom_fields so table cells resolve.
      await refreshMessageDrafts();

      // Send the edited message to continue the conversation
      await sendChatMessage({
        message: newContent,
        campaignId,
        templateId: selectedTemplate?.id || null
      });

    } catch (error) {
      console.error('Edit and continue error:', error);
      
      toast({
        title: 'Edit Failed',
        description: error.response?.data?.detail || 'Failed to edit message and continue',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  }, [editingContent, isLoading, messages, campaignId, selectedTemplate, toast, cancelEdit, sendChatMessage, setMessages, refreshMessageDrafts]);

  // Auto-send welcome message for new users - run only once on mount
  useEffect(() => {
    const WELCOME_MESSAGE = "I'm new to gillyreach tell me about yourself";
    
    if (autoSendWelcomeMessage && !welcomeMessageSentRef.current && !campaignId) {
      welcomeMessageSentRef.current = true;
      
      const sendWelcomeMessage = async () => {
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Set creating flag since this is a new campaign
        isCreatingCampaignRef.current = true;
        
        addMessage('user', WELCOME_MESSAGE);
        setIsLoading(true);
        setIsProcessing(true);

        try {
          await sendChatMessage({
            message: WELCOME_MESSAGE,
            campaignId: undefined, // Always undefined for new campaign
          });
        } catch (error) {
          console.error('Welcome message error:', error);
          addMessage('system', '❌ Sorry, there was an error. Please try again.');
          // Clear the creating flag on error
          isCreatingCampaignRef.current = false;
        } finally {
          setIsLoading(false);
          setIsProcessing(false);
        }
      };
      
      sendWelcomeMessage();
    }
    // Run only on mount - capture initial values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Status bar callback handlers.
  // completedOpId is the operation_id of the operation that just reached 100 %.
  // We clear only that slot from the operations map so that any other concurrent
  // operation's status bar continues to show correctly.
  const handleStatusBarComplete = async (completedOpId) => {
    if (isHandlingCompletionRef.current) {
      return;
    }
    
    isHandlingCompletionRef.current = true;
    
    try {
      if (campaignId) {
        await loadChatHistory();
        
        if (refreshHandlerRef.current?.initialFetch) {
          try {
            await refreshHandlerRef.current.initialFetch();
          } catch (error) {
            console.error('Error refreshing data on operation completion:', error);
          }
        }
        try {
          await refreshMessageDrafts();
        } catch (_) {
          /* non-critical */
        }
      }
      
      if (statusBarHideTimeoutRef.current) {
        clearTimeout(statusBarHideTimeoutRef.current);
      }
      
      statusBarHideTimeoutRef.current = setTimeout(() => {
        // Remove only the completed operation; if another is still running, the
        // status bar will seamlessly switch to showing its progress.
        clearOperationStatus(completedOpId);
        statusBarHideTimeoutRef.current = null;
      }, 2000);
    } finally {
      setTimeout(() => {
        isHandlingCompletionRef.current = false;
      }, 2000);
    }
  };

  // True whenever the chat SSE is open OR a backend fanout job is in progress.
  // Drives the send → stop button swap in the input.
  const isGenerating =
    isStreamOpen ||
    (!!statusUpdate && parseInt(statusUpdate.completion_percentage ?? 0) < 100);

  // Stop whatever is currently running: abort the chat stream and/or cancel the
  // active fanout job (custom fields, down-selection, email drafting, etc.).
  const handleStop = useCallback(async () => {
    if (isStreamOpen) {
      stopStream();
      setIsLoading(false);
      setIsProcessing(false);
    }
    // Abort any in-flight custom-field SSE and clear its local state so the
    // stream cannot re-surface a 100% status event after the user cancelled.
    customFieldOrchestration.cancelOperation?.();
    // Also abort the "Run for All" research SSE if one is in progress.
    researchStopRef.current?.();
    if (statusUpdate && campaignId) {
      const opId = statusUpdate.operation_id;
      if (opId) {
        try {
          await api.post(`/api/campaigns/${campaignId}/cancel/${opId}`);
        } catch (_) {
          // 404 means the job already finished — not an error worth surfacing.
        }
      }
      clearOperationStatus();
    }
  }, [isStreamOpen, stopStream, customFieldOrchestration, statusUpdate, campaignId, clearOperationStatus]);

  // Calculate right offset for left panel based on chat state
  // Use stable values: on desktop, default to expanded (400px) to prevent layout shift
  const effectiveChatExpanded = isMobile || isChatExpanded;
  const leftPanelRightOffset = isMobile ? 0 : (effectiveChatExpanded ? chatPanelWidth : chatNotchWidth);
  
  // Template gallery uses fixed padding so the chat panel overlays on top
  // without causing the cards to resize. Campaign results use animated padding
  // to stay clear of the chat panel.
  const showingGallery = !campaignId;
  const rightPadding = showingGallery
    ? (isMobile ? 16 : chatNotchWidth + 16)
    : (isMobile ? 16 : (leftPanelRightOffset + 16));

  // FLUID SPRING CONFIGURATION - smooth slide, no bounce
  // Matches CollapsibleChatPanel exactly for synchronized motion
  const springTransition = {
    type: "spring",
    stiffness: 300,
    damping: 35,    // Higher = no bounce, smooth stop
    mass: 0.8,
  };

  return (
    <Box 
      display="flex"
      flexDirection="row"
      position="relative"
      h="100%"
      w="100%"
      maxH="100%"
      overflow="hidden"
      minH={0}
    >
      {/* Left Panel - Campaign Results */}
      <motion.div
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          padding: '4px 4px 4px 4px',
          minHeight: 0,
          maxHeight: '100%',
          background: '#0a0a0a',
          // Disable interaction only when inbox is open but no thread is selected
          pointerEvents: (panelMode === 'inbox' && !selectedInboxThread) ? 'none' : undefined,
        }}
        initial={false}
        animate={{
          paddingRight: rightPadding,
          // Hidden when inbox open with no thread; visible for normal mode or when thread selected
          opacity: (panelMode === 'inbox' && !selectedInboxThread) ? 0 : 1,
        }}
        transition={
          enableTransitions
            ? { ...springTransition, opacity: { duration: 0.18, ease: 'easeInOut' } }
            : { duration: 0 }
        }
      >
        {isDetectingTemplate ? (
          <Box h="100%" w="100%" minH={0} />
        ) : (campaignId || isProcessing) ? (
          <Box h="100%" w="100%" minH={0} position="relative">
            <VirtualizedPersonaResults
              campaignId={campaignId}
              isProcessing={isProcessing}
              shouldFetchInitialData={false}
              onStreamingDataUpdate={(handler) => { streamingDataHandlerRef.current = handler; }}
              onCampaignComplete={(handler) => { campaignCompletionHandlerRef.current = handler; }}
              onRefreshReady={(handler) => { refreshHandlerRef.current = handler; }}
              onHasDataChange={setCampaignHasEntities}
              onCustomFieldGenerate={customFieldOrchestration.generateFields}
              onCustomFieldApprove={customFieldOrchestration.approvePreview}
              chatCreditGateSession={chatCreditGate}
              onSubmitChatCreditGate={submitChatCreditGate}
              onEmailStatusUpdate={(status, type) => {
                applyOperationStatus(status, type);
              }}
              onResearchStatusUpdate={(status, type) => {
                applyOperationStatus(status, type);
              }}
              isStatusBarVisible={!!statusUpdate}
              messageDrafts={messageDrafts}
              exportTracking={exportTracking}
              onExportTrackingRefresh={refreshExportTracking}
              onMessageDraftEdit={handleMessageDraftEdit}
              onMessageDraftRename={handleMessageDraftRename}
              onMessageDraftDelete={handleMessageDraftDelete}
              onMessageDraftDescriptionUpdate={handleMessageDraftDescriptionUpdate}
              researchStopRef={researchStopRef}
            />
            {/* Thread view overlays VPR when a thread is selected from the inbox.
                Rendered here (rather than replacing VPR in the ternary) so VPR
                stays mounted and retains its data across inbox ↔ chat switches. */}
            {panelMode === 'inbox' && selectedInboxThread && (
              <Box position="absolute" inset={0} zIndex={20} bg="#0a0a0a">
                <ThreadView thread={selectedInboxThread} campaignId={campaignId} />
              </Box>
            )}

            {/* Scribeless: template picker (left) + animated paper (center of remaining space) */}
            {panelMode === 'scribeless' && (
              <motion.div
                key="scribeless-paper"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  background: '#0a0a0a',
                  zIndex: 100,
                }}
              >
                {/* ── Template selector (vertically centered in the left strip) ── */}
                <div style={{ width: '160px', flexShrink: 0, alignSelf: 'stretch', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', paddingLeft: '28px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.28)', fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', alignSelf: 'center', textAlign: 'center' }}>
                    Template
                  </span>
                  {[
                    {
                      id: 'signature',
                      label: 'Signature',
                      renderThumb: (sel) => (
                        <div style={{ width: '88px', height: '64px', background: '#ffffff', borderRadius: '4px', flexShrink: 0, position: 'relative', overflow: 'hidden', border: sel ? '2px solid rgba(157,113,253,0.85)' : '1.5px solid rgba(255,255,255,0.07)', boxShadow: sel ? '0 0 0 3px rgba(157,113,253,0.15), 0 4px 16px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.4)', transition: 'box-shadow 0.15s ease, border-color 0.15s ease' }}>
                          {[10, 20, 30, 40, 50].map((t) => (
                            <div key={t} style={{ position: 'absolute', left: '8px', right: '8px', top: `${t}px`, height: '1px', background: 'rgba(0,0,0,0.07)' }} />
                          ))}
                        </div>
                      ),
                    },
                    {
                      id: 'linen',
                      label: 'Linen',
                      // thumbnail: warm ivory, elegant inset border, no lines
                      renderThumb: (sel) => (
                        <div style={{ width: '88px', height: '64px', background: '#f9f5ee', borderRadius: '4px', flexShrink: 0, position: 'relative', overflow: 'hidden', border: sel ? '2px solid rgba(157,113,253,0.85)' : '1.5px solid rgba(255,255,255,0.07)', boxShadow: sel ? '0 0 0 3px rgba(157,113,253,0.15), 0 4px 16px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.4)', transition: 'box-shadow 0.15s ease, border-color 0.15s ease' }}>
                          {/* inset double frame */}
                          <div style={{ position: 'absolute', inset: '4px', border: '1px solid rgba(139,105,60,0.18)', borderRadius: '2px' }} />
                          <div style={{ position: 'absolute', inset: '7px', border: '0.5px solid rgba(139,105,60,0.10)', borderRadius: '1px' }} />
                          {/* ghost lines */}
                          {[28, 38, 48].map((t) => (
                            <div key={t} style={{ position: 'absolute', left: '14px', right: '14px', top: `${t}px`, height: '1px', background: 'rgba(139,105,60,0.12)' }} />
                          ))}
                        </div>
                      ),
                    },
                  ].map((tpl) => {
                    const sel = scribelessTemplate === tpl.id;
                    return (
                      <button key={tpl.id} onClick={() => setScribelessTemplate(tpl.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '7px', width: '100%' }}>
                        {tpl.renderThumb(sel)}
                        <span style={{ fontSize: '11px', fontWeight: sel ? '600' : '400', color: sel ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.32)', transition: 'color 0.15s ease' }}>
                          {tpl.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* ── Paper (centered in remaining space) ── */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '18px' }}>

                  {/* ── Front / Back toggle ── */}
                  <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: '8px', padding: '3px' }}>
                    {['front', 'back'].map((side) => {
                      const active = scribelessSide === side;
                      return (
                        <button
                          key={side}
                          onClick={() => setScribelessSide(side)}
                          style={{
                            background: active ? 'rgba(255,255,255,0.10)' : 'transparent',
                            color: active ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)',
                            border: 'none', cursor: 'pointer',
                            padding: '5px 18px', borderRadius: '6px',
                            fontSize: '12px', fontWeight: active ? 600 : 400,
                            textTransform: 'capitalize', transition: 'all 0.15s ease',
                          }}
                        >
                          {side}
                        </button>
                      );
                    })}
                  </div>

                  {/* Outer box reserves the *scaled* footprint so the enlarged
                      card stays centered without overlapping the toggle above. */}
                  <motion.div
                    animate={{
                      width: (scribelessOrientation === 'landscape' ? 460 : 300) * SCRIBELESS_CARD_SCALE,
                      height: (scribelessOrientation === 'landscape' ? 310 : 420) * SCRIBELESS_CARD_SCALE,
                    }}
                    transition={{ type: 'spring', stiffness: 260, damping: 28 }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                  >
                  <motion.div
                    animate={{
                      width: scribelessOrientation === 'landscape' ? 460 : 300,
                      height: scribelessOrientation === 'landscape' ? 310 : 420,
                      scale: SCRIBELESS_CARD_SCALE,
                    }}
                    transition={{ type: 'spring', stiffness: 260, damping: 28 }}
                    onAnimationComplete={fitNoteToBox}
                    style={{
                      background: scribelessTemplate === 'linen' ? '#f9f5ee' : '#ffffff',
                      borderRadius: '5px',
                      boxShadow: '0 12px 48px rgba(0,0,0,0.40), 0 2px 8px rgba(0,0,0,0.22)',
                      position: 'relative', overflow: 'hidden',
                      transformOrigin: 'center center',
                    }}
                  >
                    {/* ── Linen: ivory + double inset border (both sides) ── */}
                    {scribelessTemplate === 'linen' && (<>
                      <div style={{ position: 'absolute', inset: '20px', border: '1px solid rgba(139,105,60,0.20)', borderRadius: '2px', pointerEvents: 'none' }} />
                      <div style={{ position: 'absolute', inset: '24px', border: '0.5px solid rgba(139,105,60,0.10)', borderRadius: '1px', pointerEvents: 'none' }} />
                    </>)}

                    {/* ══ FRONT SIDE ══ */}
                    {scribelessSide === 'front' && (<>
                      {/* Ghost handwriting strokes — only when no message is selected */}
                      {!scribelessNoteBody && (
                        <svg width="100%" height="100%" viewBox="0 0 400 300" preserveAspectRatio="none" style={{ position: 'absolute', top: 0, left: 0, opacity: 0.07, pointerEvents: 'none' }} fill="none">
                          <path d="M40 100 Q120 92 200 100 Q280 108 360 96" stroke="#1a1a1a" strokeWidth="3" strokeLinecap="round"/>
                          <path d="M40 130 Q140 122 220 130 Q300 138 360 124" stroke="#1a1a1a" strokeWidth="3" strokeLinecap="round"/>
                          <path d="M40 160 Q130 152 210 160 Q290 168 350 154" stroke="#1a1a1a" strokeWidth="3" strokeLinecap="round"/>
                          <path d="M40 190 Q120 182 200 190 Q270 196 330 184" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round"/>
                          <path d="M40 220 Q110 212 180 220 Q240 226 300 214" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round"/>
                        </svg>
                      )}

                      {/* Selected message body — handwritten letter on the front */}
                      {scribelessNoteBody && (
                        <div
                          ref={noteTextRef}
                          style={{
                            position: 'absolute',
                            top: scribelessTemplate === 'linen' ? '52px' : '44px',
                            left: scribelessTemplate === 'linen' ? '40px' : '32px',
                            right: scribelessTemplate === 'linen' ? '40px' : '32px',
                            bottom: scribelessBrand?.qr ? '74px' : '40px',
                            overflow: 'hidden',
                            fontFamily: "'Caveat', 'Segoe Script', 'Bradley Hand', cursive",
                            fontSize: `${noteFontPx}px`,
                            lineHeight: `${Math.round(noteFontPx * NOTE_LINE_RATIO)}px`,
                            fontFeatureSettings: "'calt' 1",
                            color: scribelessTemplate === 'linen' ? '#3a2f1f' : '#1f2937',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            pointerEvents: 'none',
                          }}
                        >
                          {scribelessNoteBody}
                        </div>
                      )}

                      {/* QR — rendered from the destination URL so the preview is scannable.
                          Scribeless replaces this with a tracked QR at actual print time. */}
                      {scribelessBrand?.qr && (
                        <div style={{ position: 'absolute', right: scribelessTemplate === 'linen' ? '40px' : '32px', bottom: scribelessTemplate === 'linen' ? '36px' : '28px', pointerEvents: 'none' }}>
                          <QRCodeSVG
                            value={scribelessBrand.qr}
                            size={34}
                            bgColor="transparent"
                            fgColor={scribelessTemplate === 'linen' ? '#3a2f1f' : '#1f2937'}
                            level="H"
                          />
                        </div>
                      )}
                    </>)}

                    {/* ══ BACK SIDE — logo + tagline centered in the middle ══ */}
                    {scribelessSide === 'back' && (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '0 40px', pointerEvents: 'none' }}>
                        {scribelessBrand?.logo && (
                          <img
                            src={scribelessBrand.logo}
                            alt="Brand logo"
                            style={{ maxHeight: '64px', maxWidth: '55%', objectFit: 'contain' }}
                          />
                        )}
                        {scribelessBrand?.tagline && (
                          <span style={{ fontSize: '12px', fontWeight: 500, letterSpacing: '0.02em', color: scribelessTemplate === 'linen' ? 'rgba(90,68,38,0.85)' : 'rgba(26,26,26,0.7)', textAlign: 'center', lineHeight: 1.4, maxWidth: '80%' }}>
                            {scribelessBrand.tagline}
                          </span>
                        )}
                        {!scribelessBrand?.logo && !scribelessBrand?.tagline && (
                          <span style={{ fontSize: '11px', color: 'rgba(0,0,0,0.22)', textAlign: 'center', maxWidth: '70%', lineHeight: 1.5 }}>
                            Set up your logo &amp; tagline in Brand &amp; Card Setup to show them here
                          </span>
                        )}
                      </div>
                    )}
                  </motion.div>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </Box>
        ) : (
          <TemplateGallery onTemplateSelect={handleTemplateSelect} />
        )}

      </motion.div>

      {/* Right Panel - Collapsible Chat / Message Drafting */}
      <CollapsibleChatPanel
        messages={messages}
        isAgentThinking={(isStreamOpen && !isStreaming) || isReconnectedThinking}
        messagesEndRef={messagesEndRef}
        inputRef={inputRef}
        inputMessage={inputMessage}
        onInputChange={handleTextareaChange}
        onSendMessage={handleSendMessage}
        isLoading={isLoading}
        isGenerating={isGenerating}
        onStop={handleStop}
        isTextareaAtMaxHeight={isTextareaAtMaxHeight}
        statusUpdate={statusUpdate}
        onStatusComplete={handleStatusBarComplete}
        statusOperationType={statusOperationType}
        editingState={{
          editingMessageId,
          editingContent,
          updateContent,
          handleSubmitEdit,
          cancelEdit,
          startEdit,
          editBubbleRef,
          editTextareaRef,
        }}
        isExpanded={isChatExpanded}
        onToggle={handleToggleChatPanel}
        isMobile={isMobile}
        enableTransitions={enableTransitions}
        panelMode={panelMode}
        onModeChange={handlePanelModeChange}
        campaignId={campaignId}
        onMessageSave={handleMessageSaveOrUpdate}
        onMessagePreview={handleMessagePreview}
        editingDraft={editingDraft}
        onCancelEditDraft={() => { setEditingDraft(null); setPanelMode('chat'); }}
        onFileUpload={handleFileUpload}
        isUploading={isUploading}
        canUpload={true}
        onExportTrackingRefresh={refreshExportTracking}
        onGillyreachSequencerStatusRefresh={refreshGillyreachSequencerContactStatus}
        onThreadSelect={setSelectedInboxThread}
        selectedThreadId={selectedInboxThread?.thread_id ?? null}
        creditSpendGate={chatCreditGate}
        onCreditSpendGateDecision={submitChatCreditGate}
        scribelessOrientation={scribelessOrientation}
        scribelessTemplate={scribelessTemplate}
        onScribelessOrientationChange={setScribelessOrientation}
        onScribelessTemplateChange={setScribelessTemplate}
        onScribelessBrandChange={setScribelessBrand}
        onScribelessNoteChange={setScribelessNoteBody}
      />

      {/* Refresh progress: floating glass card over chat panel */}
      {customFieldOrchestration.refreshProgress?.isRefreshing &&
       effectiveChatExpanded &&
       panelMode === 'chat' && (
        <Box
          position="absolute"
          right={0}
          top={0}
          bottom={0}
          w={`${chatPanelWidth}px`}
          display="flex"
          flexDirection="column"
          justifyContent="flex-end"
          paddingBottom="150px"
          pointerEvents="none"
          zIndex={1002}
        >
          <Box pointerEvents="auto">
            <RefreshProgressCard
              progress={customFieldOrchestration.refreshProgress}
              onComplete={customFieldOrchestration.clearRefreshProgress}
            />
          </Box>
        </Box>
      )}

      {/* Credit warning modal for SEC Run for All */}
      {warningData && (
        <CreditWarningModal
          isOpen={isWarningOpen}
          onClose={closeWarning}
          operationType={warningData.operation_type}
          creditsNeeded={warningData.credits_needed}
          currentBalance={warningData.balance}
          onConfirm={handleConfirm}
          onOptOut={handleOptOut}
          userSubscriptionStatus={userSubscriptionStatus}
          allowsOverageProceed={warningData.preview_allows_overage_proceed === true}
          overageCreditsForRequest={warningData.overage_credits_for_request}
          overagePricePerCreditCents={warningData.overage_price_per_credit_cents}
        />
      )}
    </Box>
  );
};

export default CampaignChatInterface; 
